import React, { useMemo, useState, useEffect } from "react";
import { Modal, Result, App, Spin } from "antd";
import { DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { PDFViewer } from "@ifa/ui";
import { readFile, getFileBlob } from "@ifa/utils";
import CustomButton from "../CustomButton";

export type FileType = "pdf" | "image" | "video" | "other";

export interface FilePreviewModalProps {
  /** 是否显示 Modal */
  open: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 文件路径（ossPath 或 path，用于预览和下载） */
  fileUrl?: string;
  /** 文件名 */
  fileName?: string;
  /** 文件类型（可选，会自动识别） */
  fileType?: FileType;
  /** Modal 宽度 */
  width?: string | number;
  /** 是否显示下载按钮 */
  showDownload?: boolean;
}

/**
 * 根据文件名或 URL 判断文件类型
 */
export function detectFileType(fileUrl: string, fileName?: string): FileType {
  const target = (fileName || fileUrl).toLowerCase();

  if (/\.pdf$/i.test(target) || fileUrl.startsWith("data:application/pdf")) {
    return "pdf";
  }

  if (/\.(jpe?g|png|gif|bmp|webp|svg)$/i.test(target) || fileUrl.startsWith("data:image/")) {
    return "image";
  }

  if (/\.(mp4|webm|ogg|mov)$/i.test(target) || fileUrl.startsWith("data:video/")) {
    return "video";
  }

  return "other";
}

/**
 * 从 Ant Design UploadFile 对象中提取预览信息
 */
export function getFilePreviewInfo(file: UploadFile): {
  url: string;
  name: string;
} {
  const url = file.url || (file.preview as string) || (file.response as string) || (file as any).path || "";

  const name = file.name || url.split("/").pop() || "unnamed";

  return { url, name };
}

/**
 * 文件预览 Modal 组件
 *
 * @example
 * ```tsx
 * const [previewOpen, setPreviewOpen] = useState(false);
 * const [previewFile, setPreviewFile] = useState<{ url: string; name: string }>();
 *
 * <FilePreviewModal
 *   open={previewOpen}
 *   onClose={() => setPreviewOpen(false)}
 *   fileUrl={previewFile?.url}
 *   fileName={previewFile?.name}
 * />
 * ```
 */
export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({ open, onClose, fileUrl, fileName = "File Preview", fileType: propsFileType, width = "80vw", showDownload = true }) => {
  const { message } = App.useApp();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const fileType = useMemo(() => {
    if (!fileUrl) return "other";
    return propsFileType || detectFileType(fileUrl, fileName);
  }, [fileUrl, fileName, propsFileType]);

  // 加载预览 URL（通过 API 获取文件流，不能直接使用 OSS 地址）
  useEffect(() => {
    let mounted = true;
    let blobUrl: string | null = null;

    const loadPreview = async () => {
      if (!fileUrl) {
        setPreviewUrl(null);
        return;
      }

      // 如果是 blob URL（本地预览），直接使用
      if (fileUrl.startsWith("blob:")) {
        setPreviewUrl(fileUrl);
        setIsLoadingPreview(false);
        return;
      }

      // 如果是 data URI，直接使用
      if (fileUrl.startsWith("data:")) {
        setPreviewUrl(fileUrl);
        setIsLoadingPreview(false);
        return;
      }

      // 如果是 HTTP URL，可能是已签名的 URL，直接使用
      // 但如果是 OSS 路径，需要通过 API 获取
      if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
        setPreviewUrl(fileUrl);
        setIsLoadingPreview(false);
        return;
      }

      // OSS 路径：通过 API 获取 blob URL
      setIsLoadingPreview(true);
      try {
        const result = await getFileBlob(fileUrl);
        if (mounted) {
          blobUrl = result.url;
          setPreviewUrl(blobUrl);
        }
      } catch (err) {
        console.error("Failed to load file:", err);
        if (mounted) {
          setPreviewUrl(null);
          message.error("檔案載入失敗");
        }
      } finally {
        if (mounted) {
          setIsLoadingPreview(false);
        }
      }
    };

    loadPreview();

    return () => {
      mounted = false;
      // 只清理通过 getFileBlob 创建的 blob URL
      // 不清理传入的 blob URL，因为它是外部管理的
      if (blobUrl && !fileUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [fileUrl, message]);

  const handleDownload = async () => {
    if (!fileUrl) {
      message.error("檔案路徑不可用");
      return;
    }

    try {
      // HTTP URL：直接使用 window.open 下载
      if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
        window.open(fileUrl, "_blank");
        message.success("開始下載");
        return;
      }

      // base64 图片：创建临时链接下载
      if (fileUrl.startsWith("data:")) {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = fileName || "download";
        link.click();
        message.success("開始下載");
        return;
      }

      // 其他情况：使用 readFile API
      await readFile(fileUrl, fileName);
      message.success("開始下載");
    } catch (err) {
      console.error("Download failed:", err);
      message.error("下載失敗");
    }
  };

  const renderContent = () => {
    if (isLoadingPreview) {
      return (
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "400px", gap: "16px" }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
          <p style={{ color: "#666", margin: 0 }}>載入中...</p>
        </div>
      );
    }

    if (!fileUrl || !previewUrl) {
      return <Result status="404" title="No file URL provided" subTitle="Unable to preview file." />;
    }

    if (fileType === "pdf") {
      return (
        <div style={{ width: "100%", display: "flex", flexDirection: "column" }}>
          <PDFViewer file={previewUrl} mode="pagination" />
        </div>
      );
    }

    if (fileType === "image") {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            minHeight: "400px",
            maxHeight: "70vh",
            overflow: "auto",
            backgroundColor: "#fafafa",
            padding: "16px",
          }}
        >
          <img
            src={previewUrl}
            alt={fileName}
            style={{
              width: "100%",
              height: "auto",
              objectFit: "contain",
            }}
          />
        </div>
      );
    }

    if (fileType === "video") {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
            maxHeight: "60vh",
            backgroundColor: "#000",
          }}
        >
          <video src={previewUrl} controls style={{ maxWidth: "100%", maxHeight: "100%" }}>
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    return (
      <Result
        status="info"
        title="Preview not available"
        subTitle={`File type not supported for preview: ${fileName}`}
        extra={showDownload && <CustomButton bgColor="#00C7B6" icon={<DownloadOutlined />} onClick={handleDownload} />}
      />
    );
  };

  return (
    <Modal
      title={fileName}
      open={open}
      onCancel={onClose}
      width={width}
      footer={showDownload && fileUrl ? <CustomButton icon={<DownloadOutlined />} onClick={handleDownload} bgColor="#fff" color="#666" style={{ border: "1px solid #d9d9d9" }} /> : null}
      centered
      destroyOnHidden
      styles={{
        body: {
          padding: 0,
          maxHeight: "70vh",
          overflow: "auto",
        },
      }}
    >
      {renderContent()}
    </Modal>
  );
};

export default FilePreviewModal;
